

import {createSlice, current} from '@reduxjs/toolkit';
import {DEFAULT_PAGINATION} from 'src/hooks/common-data-service/cached-table';
import {BULK_IMPORT_USERS_STEPS} from 'src/modules/bulk-update/add-attendees/constants';

import type {PayloadAction} from '@reduxjs/toolkit';
import type {UpdatedData} from 'src/components/with-interaction-table/typedef';
import type {BulkImportUserType, UserBulkImportFileType} from 'src/modules/bulk-update/add-attendees/services/typedef';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {ImportOptions} from 'src/openapi/admin/models/ImportOptions';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {AppState} from 'src/store/typedef';

type InitialState = {
    step: BULK_IMPORT_USERS_STEPS,
    targetAccount: AccountShortInfo | null,
    importSettings: ImportOptions,
    templateData: BulkImportUserType[],
    file: UserBulkImportFileType[] | null,
    validateStepPagination: Pagination,

    updatedData: UpdatedData[],
};

export const initialState: InitialState = {
    step: BULK_IMPORT_USERS_STEPS.UPLOAD_FILE,
    targetAccount: null,
    templateData: [],
    validateStepPagination: DEFAULT_PAGINATION,
    importSettings: {
        replaceSearches: false,
        replaceAlerts: false,
        replaceLayouts: false,
    },
    file: null,

    updatedData: [],
};

export const addAttendeesBulkSlice = createSlice({
    name: 'attendeesBulkImport',
    initialState,
    reducers: {
        setValidateFileStep: state => {
            state.step = BULK_IMPORT_USERS_STEPS.VALIDATE_FILE;
        },
        setTemplateData: (state, action) => {
            state.templateData = action.payload;
        },
        setFile: (state, action) => {
            state.file = action.payload;
        },
        setImportSettings: (state, action) => {
            state.importSettings = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.validateStepPagination.currentPage = action.payload;
        },
        setPageSize: (state, action) => {
            state.validateStepPagination = {
                currentPage: 1,
                pageSize: action.payload,
            };
        },
        setTargetAccount: (state, action) => {
            state.targetAccount = action.payload;
        },
        updateColumnByIds: (state, action: PayloadAction<UpdatedData[]>) => {
            const previousUpdatedData = current(state).updatedData;
            const updatedData = [...action.payload];

            updatedData.map(row => {
                current(state).templateData.forEach(({id}, index) => {
                    if (id === row.rowId) {
                        row.rowId = index;
                    }
                });

                return row;
            });

            state.updatedData = [
                ...previousUpdatedData.filter(({rowId, columnId}) => !updatedData
                    .some(updatedRow => updatedRow.rowId === rowId && updatedRow.columnId === columnId)),
                ...updatedData,
            ];
        },
        updateColumnByRowIndexes: (state, action: PayloadAction<UpdatedData[]>) => {
            const previousUpdatedData = current(state).updatedData;
            const pagination = current(state).validateStepPagination;
            const paginatedUpdatedData = action.payload.map(({rowId, ...otherFields}) => ({
                ...otherFields,
                rowId: pagination.pageSize * (pagination.currentPage - 1) + rowId,
            }));

            state.updatedData = [
                ...previousUpdatedData.filter(({rowId, columnId}) => !paginatedUpdatedData
                    .some(updatedRow => updatedRow.rowId === rowId && updatedRow.columnId === columnId)),
                ...paginatedUpdatedData,
            ];
        },
        removeRecords: (state, action: PayloadAction<number[]>) => {
            const remainedUsers = current(state).templateData.filter(({id}) => !action.payload.includes(id));
            state.templateData = remainedUsers;
            const {currentPage, pageSize} = current(state).validateStepPagination;

            const possiblePage = Math.ceil(remainedUsers.length / pageSize);
            if (currentPage > possiblePage) {
                state.validateStepPagination.currentPage = possiblePage;
            }
        },
        setCountry: (state, action: PayloadAction<{ids: number[], country: DictionaryItem}>) => {
            const {ids, country} = action.payload;
            state.templateData = current(state).templateData.map(({id, ...other}) => (ids.includes(id)
                ? {id, ...other, country}
                : {id, ...other}
            ));
        },
        applyUpdatedData: state => {
            const updatedData = current(state).updatedData;
            const templateBuffer = [...current(state).templateData];

            updatedData.forEach(({rowId, columnId, value, validationError}: UpdatedData) => {
                const itemBuffer = templateBuffer[rowId];
                if (itemBuffer) {
                    templateBuffer[rowId] = {
                        ...itemBuffer,
                        [columnId]: {value, validationError},
                    };
                }
            });

            state.templateData = templateBuffer.filter(Boolean);
            state.updatedData = [];
        },
        clearUsersBulkImport: state => {
            state.step = BULK_IMPORT_USERS_STEPS.UPLOAD_FILE;
            state.targetAccount = null;
            state.validateStepPagination = DEFAULT_PAGINATION;
            state.templateData = [];
            state.updatedData = [];
            state.importSettings = {
                replaceSearches: false,
                replaceAlerts: false,
                replaceLayouts: false,
            };
            state.file = null;
        },
    },
});

export const selectBulkUserImportStep = (state: AppState) => state.addAttendees.bulkImport.step;
export const selectBulkUserImportTargetAccount = (state: AppState) => state.addAttendees.bulkImport.targetAccount;
export const selectBulkUserImportTemplateData = (state: AppState) => state.addAttendees.bulkImport.templateData;
export const selectBulkUserImportFile = (state: AppState) => state.addAttendees.bulkImport.file;
export const selectBulkUserImportImportSettings = (state: AppState) => state.addAttendees.bulkImport.importSettings;
export const selectBulkUserImportUpdatedData = (state: AppState) => state.addAttendees.bulkImport.updatedData;
export const selectBulkUserImportValidateStepPagination = (state: AppState) =>
    state.addAttendees.bulkImport.validateStepPagination;

export const {
    setValidateFileStep,
    setTemplateData,
    setImportSettings,
    clearUsersBulkImport,
    setFile,
    setTargetAccount,
    updateColumnByIds,
    updateColumnByRowIndexes,
    applyUpdatedData,
    setCurrentPage,
    setPageSize,
    removeRecords,
    setCountry,
} = addAttendeesBulkSlice.actions;
